import { APP_ROUTES } from './routes-config';
import type { RouteConfig, RouteConfigItem } from '../interfaces/routes-config.interface';
import type { UserRole } from '../enums/user-role.enum';

/**
 * The side nav, grouped by business domain.
 *
 * This file only decides *where* a page sits in the nav. Paths and role lists still come from
 * APP_ROUTES, so the nav and the route guard read the same PERMITTED_ROLES and cannot drift. A
 * section has no role list of its own: it is shown when the user can see at least one of its
 * items, so each role only ever sees the domains it works in.
 *
 * Adding a page to the nav means adding it to a section here. side-menu.config.test.ts fails if a
 * route flagged IS_MENU / IS_MENU_LOOKUP / IS_MENU_USERS (or an IS_SUBMENU child) is left out.
 */

export type SideMenuIcon =
  | 'home'
  | 'calendar'
  | 'tickets'
  | 'reservations'
  | 'operations'
  | 'sales'
  | 'properties'
  | 'marketing'
  | 'furnishing'
  | 'finance'
  | 'insights'
  | 'users';

export interface SideMenuItem {
  route: RouteConfigItem;
  /** Overrides the route's DISPLAY_NAME where it reads badly inside its section. */
  label?: string;
}

export interface SideMenuSection {
  key: string;
  label: string;
  icon: SideMenuIcon;
  items: SideMenuItem[];
}

const children = (route: RouteConfigItem) => route.CHILDREN as RouteConfig;

const operation = children(APP_ROUTES.OPERATION_MODULE);
const payments = children(APP_ROUTES.PAYMENTS_MODULE);
const finance = children(APP_ROUTES.FINANCE_MODULE);
const marketing = children(APP_ROUTES.MARKETING_MODULE);
const furnishing = children(APP_ROUTES.FURNISHING_MANAGEMENT_MODULE);
const leads = children(APP_ROUTES.LEAD_MODULE);
const teams = children(APP_ROUTES.TEAM_MODULE);
const tickets = children(APP_ROUTES.TICKETS_MODULE);
const userManagement = children(APP_ROUTES.USER_MANAGEMENT_MODULE);
const reservation = children(APP_ROUTES.RESERVATION_MODULE);

/** Shown above the sections, ungrouped: the landing pages almost every role opens first. */
export const SIDE_MENU_PINNED: SideMenuSection[] = [
  { key: 'home', label: 'Dashboard', icon: 'home', items: [{ route: APP_ROUTES.HOME_MODULE }] },
  {
    key: 'calendar',
    label: 'Calendar',
    icon: 'calendar',
    items: [{ route: APP_ROUTES.CALENDAR_MODULE }]
  },
  // Pinned, not in a domain section: every back-office role raises and works tickets.
  { key: 'tickets', label: 'Tickets', icon: 'tickets', items: [{ route: tickets.QUEUE }] }
];

export const SIDE_MENU_SECTIONS: SideMenuSection[] = [
  {
    key: 'reservations',
    label: 'Reservations',
    icon: 'reservations',
    items: [
      { route: APP_ROUTES.RESERVATION_MODULE },
      { route: APP_ROUTES.BOOKING_MODULE, label: 'Booking Requests' },
      { route: APP_ROUTES.BLOCK_DATES_MODULE },
      { route: reservation.FEEDBACK_LIST, label: 'Guest Feedback' },
      { route: APP_ROUTES.COMPANY_MODULE },
      { route: APP_ROUTES.BROKER_MODULE },
      { route: APP_ROUTES.SALES_PERSONS_MODULE }
    ]
  },
  {
    key: 'operations',
    label: 'Operations',
    icon: 'operations',
    items: [
      { route: operation.OPERATION, label: 'Operations Board' },
      { route: operation.DAILY_OPERATIONS },
      { route: operation.CX_QUEUE },
      { route: operation.GES },
      { route: operation.HOUSE_KEEPING },
      { route: operation.HOUSE_KEEPING_LIST },
      { route: operation.PROPERTY_MANAGERS },
      { route: operation.MAINTENANCE, label: 'Maintenance' }
    ]
  },
  {
    key: 'sales',
    label: 'Sales & Leads',
    icon: 'sales',
    items: [
      { route: leads.LIST },
      { route: leads.OPS },
      { route: teams.STRUCTURE, label: 'Team Structure' },
      { route: teams.LIST },
      { route: teams.ASSIGNMENT, label: 'Lead Assignment' }
    ]
  },
  {
    key: 'properties',
    label: 'Properties & Listings',
    icon: 'properties',
    items: [
      { route: APP_ROUTES.PROPERTY_MODULE },
      { route: APP_ROUTES.UNIT_TYPE_MODULE },
      { route: APP_ROUTES.UNIT_TYPE_GROUP_MODULE },
      { route: APP_ROUTES.AREA_MODULE },
      { route: APP_ROUTES.AMENITY_MODULE },
      { route: APP_ROUTES.UNIT_LIFE_CYCLE_MODULE },
      { route: APP_ROUTES.BULK_ACTIONS_MODULE }
    ]
  },
  {
    key: 'marketing',
    label: 'Marketing & Website',
    icon: 'marketing',
    items: [
      { route: APP_ROUTES.PROMOCODE_MODULE, label: 'Promo Codes' },
      { route: marketing.BLOGS },
      { route: marketing.GUEST_AUDIENCE },
      { route: APP_ROUTES.WEBSITE_SORT_MODULE }
    ]
  },
  {
    key: 'furnishing',
    label: 'Furnishing',
    icon: 'furnishing',
    items: [
      { route: furnishing.PACKAGES, label: 'Furnishing Packages' },
      { route: furnishing.REQUESTS, label: 'Furnishing Requests' }
    ]
  },
  {
    key: 'finance',
    label: 'Finance',
    icon: 'finance',
    items: [
      { route: payments.LIST, label: 'Payments' },
      { route: payments.OVERDUE },
      { route: APP_ROUTES.PAYOUT_MODULE },
      { route: APP_ROUTES.EXPENSES_MODULE },
      { route: APP_ROUTES.LANDLORD_MODULE },
      { route: finance.LANDLORD_DETAILED_REPORT }
    ]
  },
  {
    key: 'insights',
    label: 'Insights',
    icon: 'insights',
    items: [
      { route: APP_ROUTES.KPIS_MODULE, label: 'KPIs' },
      { route: APP_ROUTES.OCCUPANCY_RATE_MODULE, label: 'Occupancy Rate' },
      { route: APP_ROUTES.DAILY_AVAILABILITY_MODULE, label: 'Daily Occupancy Rate' }
    ]
  },
  {
    key: 'users',
    label: 'Users & Guests',
    icon: 'users',
    items: [
      { route: APP_ROUTES.USER_MODULE },
      { route: userManagement.GUESTS },
      { route: userManagement.STAFF },
      { route: APP_ROUTES.GUEST_MANAGEMENT_MODULE }
    ]
  }
];

export const getItemLabel = (item: SideMenuItem): string =>
  (item.label || item.route.DISPLAY_NAME || item.route.NAME).trim();

/** A route with no PERMITTED_ROLES is open to every signed-in user, as it is in the route guard. */
export const canSeeRoute = (route: RouteConfigItem, userRole: UserRole, userRoles: UserRole[]) =>
  !route.PERMITTED_ROLES ||
  route.PERMITTED_ROLES.includes(userRole) ||
  route.PERMITTED_ROLES.some((role) => userRoles?.includes(role));

/** Sections trimmed to what this user may open; sections left empty are dropped. */
export const getVisibleSections = (
  sections: SideMenuSection[],
  userRole: UserRole,
  userRoles: UserRole[]
): SideMenuSection[] =>
  sections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => canSeeRoute(item.route, userRole, userRoles))
    }))
    .filter((section) => section.items.length > 0);

/**
 * True when `pathname` is the item's page or a page under it. Matching on a segment boundary
 * keeps `/reservations` from claiming `/reservationsX`, and pairing it with findActiveItem's
 * longest-match keeps `/reservations` from also lighting up on `/reservations/feedbacks`.
 */
const matchesPath = (pathname: string, fullPath: string) =>
  pathname === fullPath || pathname.startsWith(`${fullPath}/`);

/** The single item the current page belongs to: the longest FULL_PATH that matches. */
export const findActiveItem = (
  sections: SideMenuSection[],
  pathname: string
): SideMenuItem | undefined =>
  sections
    .flatMap((section) => section.items)
    .filter((item) => matchesPath(pathname, item.route.FULL_PATH))
    .sort((a, b) => b.route.FULL_PATH.length - a.route.FULL_PATH.length)[0];
